import { getNormalizedScrollLeft, setNormalizedScrollLeft } from 'normalize-scroll-left';

const dragScrollClass = 'dragscroll';

export const destroyDragScroll = el => {
	el.removeEventListener('mousedown', el.md, 0);
	window.removeEventListener('mouseup', el.mu, 0);
	window.removeEventListener('click', el.cl, 0);
	window.removeEventListener('mousemove', el.mm, 0);

	if (!el.ts) {
		return;
	}

	el.removeEventListener('touchstart', el.ts, 0);
	window.removeEventListener('touchmove', el.tm, 0);
	window.removeEventListener('touchend', el.te, 0);
	window.removeEventListener('touchcancel', el.tc, 0);
};

const directionCache = new Map();

const getDocDir = elm => {
	// caching to avoid quering getComputedStyle which is expensive and might cause a reflow
	if (!directionCache.has(elm)) {
		directionCache.set(elm, window.getComputedStyle(elm).direction);
	}
	return directionCache.get(elm);
};

const getScrollLeft = (element, docDir = 'ltr') =>
	docDir === 'rtl' ? getNormalizedScrollLeft(element, docDir) : element.scrollLeft;

const setScrollLeft = (element, val, docDir = 'ltr') => {
	if (docDir === 'rtl') {
		setNormalizedScrollLeft(element, val, docDir);
		return;
	}
	element.scrollLeft = val;
};

export const scrollLeftTo = (element, endValue, duration = 250) => {
	const docDir = getDocDir(element);
	const startValue = getScrollLeft(element, docDir);
	const delta = endValue - startValue;
	const start = window.performance.now();
	const step = timestamp => {
		const progress = timestamp - start;
		if (progress < duration) {
			// Cosine based ease-in-out time transition-timing-function
			const diff = Math.round(((Math.cos(Math.PI * (1 + progress / duration)) + 1) / 2) * delta);
			setScrollLeft(element, startValue + diff, docDir);
			window.requestAnimationFrame(step);
		} else {
			setScrollLeft(element, endValue, docDir);
		}
	};
	window.requestAnimationFrame(step);
};

export const scrollToElement = (elm, dragElm, itemsWrapper) => {
	if (!elm) {
		return;
	}

	let target = elm;
	if (dragElm.dataset.dragtarget) {
		target = dragElm.querySelector(dragElm.dataset.dragtarget);
	} else {
		while (
			target.parentElement !== dragElm &&
			(!itemsWrapper || target.parentElement !== itemsWrapper)
		) {
			if (!target.parentElement) {
				return;
			}
			target = target.parentElement;
		}
	}

	if (!target) {
		return;
	}
	const dragSW = dragElm.scrollWidth;
	const dragOW = dragElm.offsetWidth;
	let targetOffset = target.offsetLeft + target.offsetWidth / 2;
	let { scrollLeft } = dragElm;

	// RTL returns a different offsetLeft, so first we need to normalize it
	if (getDocDir(dragElm) === 'rtl') {
		targetOffset = dragSW - (targetOffset * -1 + dragOW);
		scrollLeft = getScrollLeft(dragElm, 'rtl');
	}

	const end = Math.max(0, Math.min(dragSW - dragOW, targetOffset - dragOW / 2));

	// Transition duration should be based on distance
	const animationDuration =
		Math.round(Math.abs((scrollLeft - end) / dragElm.offsetWidth) * 1500) + 1;

	scrollLeftTo(dragElm, end, animationDuration);
};

export const createDragScroll = (dragElm, enableTouch = true, itemsWrapper) => {
	let lastClientX = 0;
	let lastClientY = 0;
	let pushed = false;
	let lastDragged = 0;

	// Set up handler functions

	// Mouse down event handler
	dragElm.md = e => {
		// Add mouse move handler on mouse down
		window.addEventListener('mousemove', dragElm.mm, 0);

		setTimeout(() => {
			// Postpone this a bit to let the click event trigger first
			pushed = true;
			lastClientX = e.clientX;
			lastClientY = e.clientY;
		}, 150);

		e.preventDefault();
		e.stopPropagation();
	};

	// Mouse up event handler
	dragElm.mu = () => {
		// Remove mouse move handler on mouse up
		window.removeEventListener('mousemove', dragElm.mm, 0);

		setTimeout(() => {
			// Postpone this a bit to let the click event trigger first
			pushed = false;
		}, 150);
	};

	// Click event handler
	dragElm.cl = e => {
		// If clicked, mouse move event handler not needed, so remove
		window.removeEventListener('mousemove', dragElm.mm, 0);

		// Clicks lasting more than ~150ms are most likely done while draggin.
		// So we ignore them, to prevent accidental selection while scrolling.
		if (Math.abs(new Date().getTime() - lastDragged) > 150) {
			scrollToElement(e.target, dragElm, itemsWrapper);
		}
	};

	// Mouse move event handler
	dragElm.mm = e => {
		if (pushed) {
			dragElm.scrollLeft -= -lastClientX + (lastClientX = e.clientX);
			dragElm.scrollTop -= -lastClientY + (lastClientY = e.clientY);
			lastDragged = new Date().getTime();
		}
	};

	// Add event listeners
	dragElm.addEventListener('mousedown', dragElm.md, 0);
	window.addEventListener('click', dragElm.cl, 0);
	window.addEventListener('mouseup', dragElm.mu, 0);

	if (!enableTouch) {
		return;
	}

	// Touch start event handler
	dragElm.ts = e => {
		pushed = true;
		lastClientX = e.touches[0].clientX;
		lastClientY = e.touches[0].clientY;
	};

	// Touch end event handler
	dragElm.te = () => {
		pushed = false;
	};

	// Touch move event handler
	dragElm.tm = e => {
		if (pushed) {
			dragElm.scrollLeft -= -lastClientX + (lastClientX = e.touches[0].clientX);
			dragElm.scrollTop -= -lastClientY + (lastClientY = e.touches[0].clientY);
			lastDragged = new Date().getTime();
		}
	};

	// Touch cancel event handler
	dragElm.tc = () => {
		pushed = false;
	};

	dragElm.addEventListener('touchstart', dragElm.ts, 0);
	window.addEventListener('touchend', dragElm.te, 0);
	window.addEventListener('touchcancel', dragElm.tc, 0);
	window.addEventListener('touchmove', dragElm.tm, 0);
};

export const dragScroll = el =>
	Array.from(el.getElementsByClassName(dragScrollClass)).forEach(element => {
		destroyDragScroll(element);
		createDragScroll(element);
	});

export default dragScroll;
